package com.crowpay

import com.lightningkite.now
import kotlinx.datetime.*
import kotlinx.datetime.format.DateTimeFormat
import kotlinx.datetime.format.DayOfWeekNames
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.format.Padding
import kotlinx.datetime.format.char
import kotlin.time.Duration
import kotlin.time.DurationUnit
import kotlin.time.toDuration

private val timeZone = TimeZone.currentSystemDefault()

fun Instant.formatLong(): String = buildString {
    val dateTime = this@formatLong.toLocalDateTime(timeZone)

    append("${dateTime.month.toString().lowercase()} ${dateTime.dayOfMonth}, ")
    append("${dateTime.year}, ")
    append(
        "${
            (dateTime.hour + 11) % 12 + 1
        }:${
            dateTime.minute.toString().padStart(2, '0')
        } ${
            if (dateTime.hour / 12 < 1) "AM" else "PM"
        }"
    )
}

fun Instant.format(formatter: DateTimeFormat<LocalDateTime>) = toLocalDateTime(timeZone).format(formatter)

private val today = now().toLocalDateTime(timeZone)
fun Instant.formatRelative(includeTimestamp: Boolean = true): String {
    val dateTime = toLocalDateTime(timeZone)

    return when (dateTime.date) {
        today.date -> {
            if (includeTimestamp) "Today ${dateTime.format(Formats.hmm)}"
            else "Today"
        }
        today.date.minus(DatePeriod(days = 1)) -> {
            if (includeTimestamp) "Yesterday ${dateTime.format(Formats.hmm)}"
            else "Yesterday"
        }
        else -> dateTime.format(
            if (includeTimestamp) Formats.mmddyyyy_hmm
            else Formats.mmddyyyy
        )
    }
}

object Formats {
    val mmddyyyy = LocalDateTime.Format {
        monthNumber(Padding.NONE)
        char('/')
        dayOfMonth(Padding.NONE)
        char('/')
        year()
    }
    val mmddyyyy_hmm = LocalDateTime.Format {
        monthNumber(Padding.NONE)
        char('/')
        dayOfMonth(Padding.NONE)
        char('/')
        year()
        char(' ')
        amPmHour(Padding.NONE)
        char(':')
        minute(Padding.ZERO)
        char(' ')
        amPmMarker("AM", "PM")
    }
    val hmm = LocalDateTime.Format {
        amPmHour(Padding.NONE)
        char(':')
        minute(Padding.ZERO)
        char(' ')
        amPmMarker("AM", "PM")
    }
    val eeeddmmyyyy_hmm = LocalDateTime.Format {
        dayOfWeek(DayOfWeekNames.ENGLISH_ABBREVIATED)
        char(',')
        char(' ')
        monthName(MonthNames.ENGLISH_ABBREVIATED)
        char(' ')
        dayOfMonth(Padding.NONE)
        char(' ')
        year()
        char(' ')
        amPmHour(Padding.NONE)
        char(':')
        minute(Padding.ZERO)
        char(' ')
        amPmMarker("AM", "PM")
    }
    val naturalEnglish = LocalDateTime.Format {
        dayOfWeek(DayOfWeekNames("Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"))
        chars(", ")
        monthName(MonthNames("January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"))
        char(' ')
        dayOfMonth(Padding.NONE)
        chars(", ")
        amPmHour(Padding.NONE)
        char(':')
        minute(Padding.ZERO)
        char(' ')
        amPmMarker("AM", "PM")
    }
}

fun Duration.formatNatural(
    includeDays: Boolean = true,
    includeHours: Boolean = true,
    includeMinutes: Boolean = true,
    includeSeconds: Boolean = true
): String {
    var remaining = this
    val parts = mutableListOf<String>()

    if (includeDays) {
        val days = remaining.inWholeDays
        if (days > 0) {
            parts += "$days day" + if (days != 1L) "s" else ""
            remaining -= days.toDuration(DurationUnit.DAYS)
        }
    }
    if (includeHours) {
        val hours = remaining.inWholeHours
        if (hours > 0) {
            parts += "$hours hour" + if (hours != 1L) "s" else ""
            remaining -= hours.toDuration(DurationUnit.HOURS)
        }
    }
    if (includeMinutes) {
        val minutes = remaining.inWholeMinutes
        if (minutes > 0) {
            parts += "$minutes minute" + if (minutes != 1L) "s" else ""
            remaining -= minutes.toDuration(DurationUnit.MINUTES)
        }
    }
    if (includeSeconds) {
        val seconds = remaining.inWholeSeconds
        if (seconds > 0 || parts.isEmpty()) { // Always show at least seconds if nothing else
            parts += "$seconds second" + if (seconds != 1L) "s" else ""
        }
    }

    return parts.joinToString(", ")
}